/**
 * RenewalsScreen — Renewals board at /renewals. Story R3.
 *
 * Replaces the previous PlaceholderScreen. First-pass display + group only.
 * Groups active policies into time-bucket columns with orange/red urgency per card.
 *
 * Data (V10-J): useRenewalsBoard() — live GET /renewals/board; mock fallback
 * (MOCK_RENEWALS_BOARD) behind the data-mode flag (lib/data-mode.ts).
 *
 * Out of scope for R3 (roadmap §49): detail sheet, Offerte-mail, toasts,
 * KI-Empfehlung, workflow status, J/K nav.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §41/§49 (R3)]
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { StickyPageHeader } from '../../components/ui/StickyPageHeader';
import { BucketColumn } from './components/BucketColumn';
import { BUCKET_META, groupByBucket } from './mock';
import { useRenewalsBoard } from './hooks/useRenewalsBoard';
import { RENEWAL_BUCKETS } from './types';
import type { RenewalBucket, RenewalBoardItemVM } from './types';

type BucketFilter = RenewalBucket | 'alle';

export function RenewalsScreen() {
  const navigate = useNavigate();
  const [activeBucket, setActiveBucket] = useState<BucketFilter>('alle');

  // Data — V10-J: live GET /renewals/board, mock fallback behind the data-mode flag
  const { data: board, isLoading, isError } = useRenewalsBoard();

  if (isLoading || isError || !board) {
    return (
      <div className="px-6 py-5">
        <h1 className="text-xl font-semibold">Renewals</h1>
        {isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Lade Renewals …</p>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground" role="alert">
            Renewals konnten nicht geladen werden.
          </p>
        )}
      </div>
    );
  }

  const grouped = groupByBucket(board.items);

  // Buckets to display in the board body
  const visibleBuckets: RenewalBucket[] =
    activeBucket === 'alle'
      ? RENEWAL_BUCKETS.filter((b) => grouped[b].length > 0)
      : [activeBucket];

  function handleKundeActivate(item: RenewalBoardItemVM) {
    if (item.kundeId) {
      void navigate({ to: '/customers/$id', params: { id: item.kundeId } });
    }
  }

  // Total + red count for optional subtitle
  const total = board.items.length;
  const redCount = board.items.filter((i) => i.status === 'mahnen').length;

  return (
    <div className="px-6 py-5">
      {/* E3: title + bucket filters pinned, so the filter stays reachable. */}
      <StickyPageHeader className="-mx-6 -mt-5 px-6 pt-5">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold">Renewals</h1>
        {total > 0 && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            {total} Policen
            {redCount > 0 && (
              <>
                {' · '}
                <span>{redCount} rot</span>
              </>
            )}
          </p>
        )}
      </div>

      {/* Bucket filter tabs */}
      <div className="mb-5 overflow-x-auto">
        <Tabs value={activeBucket} onValueChange={(v) => setActiveBucket(v as BucketFilter)}>
          <TabsList>
            <TabsTrigger value="alle">
              Alle
              <span className="ml-1 text-muted-foreground tabular-nums">{total}</span>
            </TabsTrigger>
            {RENEWAL_BUCKETS.map((b) => {
              // Only show 'unknown' tab if there are unknown items (avoid clutter)
              if (b === 'unknown' && board.bucketCounts.unknown === 0) return null;
              return (
                <TabsTrigger key={b} value={b}>
                  {BUCKET_META[b].label}
                  {board.bucketCounts[b] > 0 && (
                    <span className="ml-1 text-muted-foreground tabular-nums">
                      {board.bucketCounts[b]}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>
      </StickyPageHeader>

      {/* Board grid */}
      {visibleBuckets.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Keine Renewals in diesem Zeitfenster.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {visibleBuckets.map((b) => (
            <BucketColumn
              key={b}
              bucket={b}
              label={BUCKET_META[b].label}
              items={grouped[b]}
              onKundeActivate={handleKundeActivate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
